import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check, Mail, MessageCircle, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import NavShell from '../components/NavShell';
import { supabase } from '../lib/supabase';
import { trackReferralSent } from '../lib/analytics';
import { API_BASE } from '../lib/apiBase';

async function fetchReferralStats() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}/api/ua/referral-stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch referral stats');
  return res.json() as Promise<{
    referralCode: string;
    referralLink: string;
    friendsInvited: number;
    friendsConverted: number;
    pointsEarned: number;
  }>;
}

export default function ReferralPage() {
  const { data, isLoading } = useQuery({ queryKey: ['referral-stats'], queryFn: fetchReferralStats });
  const [copied, setCopied] = useState(false);

  const link = data?.referralLink ?? '';

  // GA4: referral_sent fires whenever the member shares the link via any of
  // the supported channels (copy, email, WhatsApp, Twitter). One event per
  // intent to share — duplicates across channels are intentional signal.
  function fireReferral() {
    try { trackReferralSent(); } catch { /* analytics non-fatal */ }
  }

  function handleCopy() {
    void navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    fireReferral();
  }

  function handleEmail() {
    const subject = encodeURIComponent('Join me on Swipe Opinion and earn rewards!');
    const body = encodeURIComponent(`I've been using Swipe Opinion to earn gift cards and cash by taking quick surveys and playing games. Join through my link and you'll get 25 bonus points: ${link}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    fireReferral();
  }

  function handleWhatsApp() {
    const text = encodeURIComponent(`Join me on Swipe Opinion! Earn rewards for surveys and games. Get 25 bonus points when you sign up: ${link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    fireReferral();
  }

  function handleTwitter() {
    const text = encodeURIComponent(`Earning rewards on @SwipeOpinion for sharing my opinions. Join me and get 25 bonus points: ${link}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    fireReferral();
  }

  return (
    <NavShell>
      <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-primary-dark p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative">
            <h1 className="text-2xl sm:text-3xl font-bold font-display">Invite Friends, Earn Together</h1>
            <p className="text-purple-200 mt-2 text-sm max-w-lg">
              You earn 50 pts for every friend who completes their first survey. They get 25 pts just for joining through your link.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 h-32 animate-pulse" />
        ) : data ? (
          <>
            {/* Referral Link */}
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
              <h2 className="text-lg font-bold font-display text-[#111827]">Your Referral Link</h2>
              <div className="flex gap-2">
                <input
                  value={link}
                  readOnly
                  className="flex-1 bg-[#F8F9FA] text-primary font-mono text-sm rounded-xl px-4 py-3 border border-[#E5E7EB] focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="bg-primary text-white px-5 py-3 rounded-xl text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-2"
                >
                  {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
                </button>
              </div>

              {/* Share buttons */}
              <div className="flex flex-wrap gap-3">
                <button onClick={handleCopy} className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2.5 rounded-full text-sm font-medium hover:bg-primary hover:text-white transition-colors">
                  <Copy className="w-4 h-4" /> Copy Link
                </button>
                <button onClick={handleEmail} className="flex items-center gap-2 bg-[#F8F9FA] text-[#111827] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors">
                  <Mail className="w-4 h-4" /> Email
                </button>
                <button onClick={handleWhatsApp} className="flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#25D366] hover:text-white transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </button>
                <button onClick={handleTwitter} className="flex items-center gap-2 bg-[#F8F9FA] text-[#111827] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors">
                  𝕏 Post
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-bold font-display text-[#111827] mb-4">Your Referral Stats</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#111827] tabular-nums">{data.friendsInvited}</p>
                  <p className="text-xs text-[#6B7280] mt-1">Friends Invited</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#10B981] tabular-nums">{data.friendsConverted}</p>
                  <p className="text-xs text-[#6B7280] mt-1">Completed Survey</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary tabular-nums">{data.pointsEarned}</p>
                  <p className="text-xs text-[#6B7280] mt-1">Points Earned</p>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-bold font-display text-[#111827] mb-4">How It Works</h2>
              <div className="space-y-4">
                {[
                  { step: 1, text: 'Share your unique link with friends' },
                  { step: 2, text: 'They sign up and complete their profile' },
                  { step: 3, text: 'When they finish their first survey, you both earn points' },
                  { step: 4, text: 'No limit — keep referring!' },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
                      {s.step}
                    </span>
                    <p className="text-sm text-[#111827] pt-1">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </motion.main>
    </NavShell>
  );
}
